import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Lock, Layers, Plus, DollarSign, Wallet, TrendingUp, 
  Settings, ArrowLeft, Notebook, MoreHorizontal, Trash2,
  Star, Eye, EyeOff, Calculator, PiggyBank, Target, BookOpen,
  ChevronRight, FileText, Table2, ListChecks, Check, Square,
  CheckSquare, GripVertical, Palette, CreditCard, CalendarDays,
  Receipt, RotateCcw, Zap, X, Pencil, ChevronDown, ChevronUp,
  AlertCircle, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import { getCurrentUserId } from '../utils/currentUser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DP63 Part 2 — the name literals are gone; only the id is real.
// DP64 Part 6b — the four possessive headings are "Your …", and this file no
// longer reads a name at all. useOwnName's 'there' fallback rendered
// "there's Work Tables"; "Your" is correct for whoever is signed in and needs
// no fallback.
const CURRENT_USER = {
  id: getCurrentUserId(),
};

const LIST_COLORS = [
  { name: 'Blue', value: 'blue', bg: 'bg-blue-500', light: 'bg-blue-50', dark: 'bg-blue-900/20', text: 'text-blue-600', darkText: 'text-blue-400' },
  { name: 'Green', value: 'green', bg: 'bg-emerald-500', light: 'bg-emerald-50', dark: 'bg-emerald-900/20', text: 'text-emerald-600', darkText: 'text-emerald-400' },
  { name: 'Orange', value: 'orange', bg: 'bg-orange-500', light: 'bg-orange-50', dark: 'bg-orange-900/20', text: 'text-orange-600', darkText: 'text-orange-400' },
  { name: 'Purple', value: 'purple', bg: 'bg-purple-500', light: 'bg-purple-50', dark: 'bg-purple-900/20', text: 'text-purple-600', darkText: 'text-purple-400' },
  { name: 'Red', value: 'red', bg: 'bg-red-500', light: 'bg-red-50', dark: 'bg-red-900/20', text: 'text-red-600', darkText: 'text-red-400' },
  { name: 'Yellow', value: 'yellow', bg: 'bg-amber-500', light: 'bg-amber-50', dark: 'bg-amber-900/20', text: 'text-amber-600', darkText: 'text-amber-400' },
];

const BILL_CATEGORIES = ['Housing', 'Utilities', 'Insurance', 'Transportation', 'Debt', 'Other'];
const SUB_CATEGORIES = ['Software', 'Streaming', 'Membership', 'Service', 'Other'];

export default function CornerOfDesk() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [personalWorkTables, setPersonalWorkTables] = useState([]);
  const [showNewWorkTable, setShowNewWorkTable] = useState(false);
  const [newWorkTableName, setNewWorkTableName] = useState('');
  
  // Budget
  const [budget, setBudget] = useState({
    monthly_income: 0, monthly_expenses: 0, savings_goal: 0, current_savings: 0,
    bills: [], subscriptions: []
  });
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showBudgetValues, setShowBudgetValues] = useState(false);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [billsExpanded, setBillsExpanded] = useState(false);
  const [subsExpanded, setSubsExpanded] = useState(false);
  
  const [libraryItems, setLibraryItems] = useState([]);
  
  // Quick Lists
  const [quickLists, setQuickLists] = useState([]);
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListColor, setNewListColor] = useState('blue');
  const [newItemTexts, setNewItemTexts] = useState({});
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('keyMadeDarkMode') === 'true';
    }
    return false;
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [workTablesRes, libraryRes] = await Promise.all([
        axios.get(`${API}/work-tables/personal/corner`, { 
          params: { user_id: CURRENT_USER.id } 
        }).catch(() => ({ data: { personal_work_tables: [] } })),
        axios.get(`${API}/knowledge/items`, {
          params: { visibility: 'personal', user_id: CURRENT_USER.id }
        }).catch(() => ({ data: { items: [] } }))
      ]);
      
      setPersonalWorkTables(workTablesRes.data.personal_work_tables || []);
      setLibraryItems((libraryRes.data.items || []).slice(0, 5));
      
      const savedBudget = localStorage.getItem('keyMadePersonalBudget');
      if (savedBudget) {
        const parsed = JSON.parse(savedBudget);
        setBudget({ bills: [], subscriptions: [], ...parsed });
      }
      
      const savedLists = localStorage.getItem('keyMadeQuickLists');
      if (savedLists) setQuickLists(JSON.parse(savedLists));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Persistence helpers ---
  const saveBudgetData = useCallback((data) => {
    setBudget(data);
    localStorage.setItem('keyMadePersonalBudget', JSON.stringify(data));
  }, []);

  const saveQuickLists = useCallback((lists) => {
    setQuickLists(lists);
    localStorage.setItem('keyMadeQuickLists', JSON.stringify(lists));
  }, []);

  // --- Work Tables ---
  const createPersonalWorkTable = async () => {
    try {
      const response = await axios.post(`${API}/work-tables`, {
        user_id: CURRENT_USER.id,
        name: newWorkTableName || 'Personal Work Table'
      });
      await axios.put(`${API}/work-tables/${response.data.id}/personal`, null, {
        params: { is_personal: true }
      });
      toast.success('Personal work table created');
      setShowNewWorkTable(false);
      setNewWorkTableName('');
      navigate(`/work-tables/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create work table');
    }
  };

  const deleteWorkTable = async (id) => {
    try {
      await axios.delete(`${API}/work-tables/${id}`);
      setPersonalWorkTables(prev => prev.filter(wt => wt.id !== id));
      toast.success('Work table deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // --- Quick Lists ---
  const createQuickList = () => {
    const list = {
      id: Date.now().toString(),
      name: newListName || 'New List',
      color: newListColor,
      items: [],
      created_at: new Date().toISOString()
    };
    saveQuickLists([...quickLists, list]);
    setShowNewList(false);
    setNewListName('');
    setNewListColor('blue');
    toast.success('Quick List created');
  };

  const addListItem = (listId) => {
    const text = (newItemTexts[listId] || '').trim();
    if (!text) return;
    const updated = quickLists.map(l => l.id === listId ? {
      ...l,
      items: [...l.items, { id: Date.now().toString(), text, checked: false }]
    } : l);
    saveQuickLists(updated);
    setNewItemTexts(prev => ({ ...prev, [listId]: '' }));
  };

  const toggleListItem = (listId, itemId) => {
    const updated = quickLists.map(l => l.id === listId ? {
      ...l,
      items: l.items.map(i => i.id === itemId ? { ...i, checked: !i.checked } : i)
    } : l);
    saveQuickLists(updated);
  };

  const removeListItem = (listId, itemId) => {
    const updated = quickLists.map(l => l.id === listId ? {
      ...l, items: l.items.filter(i => i.id !== itemId)
    } : l);
    saveQuickLists(updated);
  };

  const deleteQuickList = (listId) => {
    saveQuickLists(quickLists.filter(l => l.id !== listId));
    toast.success('List deleted');
  };

  // --- Bills & Subscriptions ---
  const saveBillOrSub = (type) => {
    const item = type === 'bill' ? editingBill : editingSub;
    if (!item?.name) return;
    const key = type === 'bill' ? 'bills' : 'subscriptions';
    const existing = budget[key] || [];
    const isEdit = existing.some(b => b.id === item.id);
    const updated = isEdit 
      ? existing.map(b => b.id === item.id ? item : b) 
      : [...existing, { ...item, id: Date.now().toString() }];
    saveBudgetData({ ...budget, [key]: updated });
    if (type === 'bill') { setShowBillDialog(false); setEditingBill(null); }
    else { setShowSubDialog(false); setEditingSub(null); }
    toast.success(`${type === 'bill' ? 'Bill' : 'Subscription'} saved`);
  };

  const deleteBillOrSub = (type, id) => {
    const key = type === 'bill' ? 'bills' : 'subscriptions';
    saveBudgetData({ ...budget, [key]: (budget[key] || []).filter(b => b.id !== id) });
    toast.success('Removed');
  };

  const toggleBillPaid = (id) => {
    const bills = (budget.bills || []).map(b => b.id === id ? { ...b, paid_this_month: !b.paid_this_month } : b);
    saveBudgetData({ ...budget, bills });
  };

  // --- Calculations ---
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  
  const totalBills = (budget.bills || []).reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);
  const totalSubs = (budget.subscriptions || []).reduce((s, b) => {
    const amt = parseFloat(b.amount) || 0;
    return s + (b.frequency === 'annual' ? amt / 12 : amt);
  }, 0);
  const totalMonthlyBurn = totalBills + totalSubs;
  const unpaidBills = (budget.bills || []).filter(b => !b.paid_this_month).length;
  const budgetRemaining = budget.monthly_income - budget.monthly_expenses - totalMonthlyBurn;
  const savingsProgress = budget.savings_goal > 0 ? Math.min(100, (budget.current_savings / budget.savings_goal) * 100) : 0;

  const getColor = (c) => LIST_COLORS.find(lc => lc.value === c) || LIST_COLORS[0];

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-[#F8F7F5]'} flex items-center justify-center`}>
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-[#F8F7F5]'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className={darkMode ? 'text-gray-300 hover:bg-gray-700' : ''}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-500" />
                <h1 className={`text-xl font-heading ${darkMode ? 'text-white' : ''}`}>Corner of the Desk</h1>
                <HelpIcon elementId="corner-of-desk" position="inline" />
                <Badge className={darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}>Private</Badge>
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Your personal space - only visible to you</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Work Tables + Quick Lists + Library */}
          <div className="lg:col-span-2 space-y-6">
            {/* Work Tables */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="work-tables-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                    <Notebook className="w-5 h-5 text-purple-500" />
                    Your Work Tables
                  </CardTitle>
                  <CardDescription className={darkMode ? 'text-gray-400' : ''}>Private workspaces only you can see</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowNewWorkTable(true)}
                  className={darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : ''}>
                  <Plus className="w-4 h-4 mr-2" /> New
                </Button>
              </CardHeader>
              <CardContent>
                {personalWorkTables.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {personalWorkTables.map(wt => (
                      <div key={wt.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                        onClick={() => navigate(`/work-tables/${wt.id}`)}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                          <Layers className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>{wt.name}</p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{wt.items?.length || 0} items</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className={`h-8 w-8 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : ''}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); deleteWorkTable(wt.id); }} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Lock className={`w-12 h-12 mx-auto mb-3 opacity-30 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`} />
                    <p className={`font-medium mb-2 ${darkMode ? 'text-white' : ''}`}>No personal work tables yet</p>
                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Create a private workspace for your personal ideas</p>
                    <Button onClick={() => setShowNewWorkTable(true)} className={darkMode ? 'bg-purple-600 hover:bg-purple-700' : ''}>
                      <Plus className="w-4 h-4 mr-2" /> Create Work Table
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Lists */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="quick-lists-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                    <ListChecks className="w-5 h-5 text-blue-500" />
                    Quick Lists
                  </CardTitle>
                  <CardDescription className={darkMode ? 'text-gray-400' : ''}>Fast reference lists - easy to maintain</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowNewList(true)}
                  className={darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : ''} data-testid="new-quick-list-btn">
                  <Plus className="w-4 h-4 mr-2" /> New List
                </Button>
              </CardHeader>
              <CardContent>
                {quickLists.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {quickLists.map(list => {
                      const color = getColor(list.color);
                      const total = list.items.length;
                      const done = list.items.filter(i => i.checked).length;
                      return (
                        <div key={list.id} data-testid={`quick-list-${list.id}`}
                          className={`rounded-lg border p-3 ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${color.bg}`} />
                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{list.name}</span>
                              {total > 0 && (
                                <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{done}/{total}</span>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className={`h-6 w-6 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : ''}`}>
                                  <MoreHorizontal className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => {
                                  const cleared = quickLists.map(l => l.id === list.id ? { ...l, items: l.items.filter(i => !i.checked) } : l);
                                  saveQuickLists(cleared);
                                }}>
                                  <Check className="w-4 h-4 mr-2" /> Clear Completed
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => deleteQuickList(list.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete List
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          {/* Items */}
                          <div className="space-y-1 max-h-48 overflow-y-auto mb-2">
                            {list.items.map(item => (
                              <div key={item.id} className="flex items-center gap-2 group" data-testid={`list-item-${item.id}`}>
                                <Checkbox checked={item.checked}
                                  onCheckedChange={() => toggleListItem(list.id, item.id)}
                                  className={`h-4 w-4 ${darkMode ? 'border-gray-500' : ''}`} />
                                <span className={`text-sm flex-1 ${item.checked ? 'line-through opacity-50' : ''} ${darkMode ? 'text-gray-200' : ''}`}>
                                  {item.text}
                                </span>
                                <button onClick={() => removeListItem(list.id, item.id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity">
                                  <X className={`w-3 h-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Quick Add */}
                          <div className="flex gap-1">
                            <Input value={newItemTexts[list.id] || ''} placeholder="Add item..."
                              className={`h-7 text-xs ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder:text-gray-400' : ''}`}
                              onChange={(e) => setNewItemTexts(prev => ({ ...prev, [list.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') addListItem(list.id); }}
                              data-testid={`quick-list-input-${list.id}`} />
                            <Button variant="ghost" size="sm" className={`h-7 px-2 ${darkMode ? 'text-gray-300 hover:bg-gray-600' : ''}`}
                              onClick={() => addListItem(list.id)}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ListChecks className={`w-10 h-10 mx-auto mb-2 opacity-30 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Create quick lists for easy reference</p>
                    <Button variant="outline" size="sm" onClick={() => setShowNewList(true)}
                      className={darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : ''}>
                      <Plus className="w-3 h-3 mr-1" /> Create Your First List
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Library */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="library-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                    <BookOpen className="w-5 h-5 text-amber-500" />
                    Your Library
                  </CardTitle>
                  <CardDescription className={darkMode ? 'text-gray-400' : ''}>Your personal resource library</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/knowledge')}
                    className={darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : ''}>
                    <Plus className="w-4 h-4 mr-2" /> New
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge?visibility=personal')}
                    className={darkMode ? 'text-gray-300 hover:bg-gray-700' : ''}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {libraryItems.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {libraryItems.map(item => {
                      const iconMap = { table: Table2, wiki: BookOpen, note: FileText };
                      const colorMap = { table: 'text-amber-500', wiki: 'text-blue-500', note: 'text-purple-500' };
                      const Icon = iconMap[item.content_type] || FileText;
                      const iconColor = colorMap[item.content_type] || 'text-gray-500';
                      return (
                        <div key={item.id} onClick={() => navigate(`/knowledge/${item.id}`)}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}>
                          <div className={`w-8 h-8 rounded flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-white border'}`}>
                            <Icon className={`w-4 h-4 ${iconColor}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : ''}`}>{item.title}</p>
                            <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                              {item.content_type} {item.table_rows?.length > 0 && `- ${item.table_rows.length} rows`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className={`w-12 h-12 mx-auto mb-3 opacity-50 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`} />
                    <p className={`font-medium mb-1 ${darkMode ? 'text-white' : ''}`}>No personal library items yet</p>
                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>Create notes, databases, and documents for your personal reference</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Budget + Bills + Subscriptions */}
          <div className="space-y-6">
            {/* Budget Overview */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="budget-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className={`flex items-center gap-2 text-base ${darkMode ? 'text-white' : ''}`}>
                  <Wallet className="w-5 h-5 text-emerald-500" />
                  Your Budget
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className={`h-8 w-8 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : ''}`}
                    onClick={() => setShowBudgetValues(!showBudgetValues)}>
                    {showBudgetValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className={`h-8 w-8 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : ''}`}
                    onClick={() => setShowBudgetDialog(true)}>
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  {[
                    { label: 'Monthly Income', value: budget.monthly_income, color: 'text-emerald-500' },
                    { label: 'Monthly Expenses', value: budget.monthly_expenses, color: 'text-red-500' },
                    { label: 'Bills & Subs', value: totalMonthlyBurn, color: 'text-orange-500' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="flex items-center justify-between mb-1.5 last:mb-0">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-muted-foreground'}`}>{label}</span>
                      <span className={`font-medium ${color}`}>{showBudgetValues ? formatCurrency(value) : '......'}</span>
                    </div>
                  ))}
                  <div className={`border-t pt-1.5 mt-1.5 ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : ''}`}>Remaining</span>
                      <span className={`font-bold ${budgetRemaining >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {showBudgetValues ? formatCurrency(budgetRemaining) : '......'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Savings */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : ''}`}>
                      <PiggyBank className="w-4 h-4 inline mr-1 text-blue-500" /> Savings Goal
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                      {showBudgetValues ? `${formatCurrency(budget.current_savings)} / ${formatCurrency(budget.savings_goal)}` : '......'}
                    </span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${savingsProgress}%` }} />
                  </div>
                  <p className={`text-xs mt-1 text-right ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{savingsProgress.toFixed(0)}%</p>
                </div>
              </CardContent>
            </Card>

            {/* Bills */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="bills-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 cursor-pointer"
                onClick={() => setBillsExpanded(!billsExpanded)}>
                <div className="flex items-center gap-2">
                  <CardTitle className={`flex items-center gap-2 text-base ${darkMode ? 'text-white' : ''}`}>
                    <Receipt className="w-5 h-5 text-orange-500" /> Bills
                  </CardTitle>
                  {unpaidBills > 0 && (
                    <Badge variant="destructive" className="text-[10px] h-5">{unpaidBills} due</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {showBudgetValues ? formatCurrency(totalBills) : '...'}/mo
                  </span>
                  {billsExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </CardHeader>
              {billsExpanded && (
                <CardContent className="pt-0">
                  {(budget.bills || []).length > 0 ? (
                    <div className="space-y-1.5">
                      {(budget.bills || []).map(bill => (
                        <div key={bill.id} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                          data-testid={`bill-${bill.id}`}>
                          <Checkbox checked={bill.paid_this_month} onCheckedChange={() => toggleBillPaid(bill.id)}
                            className={darkMode ? 'border-gray-500' : ''} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <span className={`font-medium truncate ${bill.paid_this_month ? 'line-through opacity-50' : ''} ${darkMode ? 'text-gray-200' : ''}`}>{bill.name}</span>
                              {bill.auto_pay && <Zap className="w-3 h-3 text-amber-500 flex-shrink-0" title="Auto-pay" />}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                Due: {bill.due_day ? `${bill.due_day}${['st','nd','rd'][((bill.due_day%100-20)%10-1)] || 'th'}` : '-'}
                              </span>
                              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{bill.category}</span>
                            </div>
                          </div>
                          <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                            {showBudgetValues ? formatCurrency(bill.amount) : '...'}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className={`h-6 w-6 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : ''}`}>
                                <MoreHorizontal className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => { setEditingBill(bill); setShowBillDialog(true); }}>
                                <Pencil className="w-3 h-3 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteBillOrSub('bill', bill.id)} className="text-red-600">
                                <Trash2 className="w-3 h-3 mr-2" /> Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-xs text-center py-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No bills added yet</p>
                  )}
                  <Button variant="outline" size="sm" className={`w-full mt-2 text-xs ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
                    onClick={() => { setEditingBill({ name: '', amount: '', due_day: '', category: 'Other', auto_pay: false, paid_this_month: false }); setShowBillDialog(true); }}
                    data-testid="add-bill-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Bill
                  </Button>
                </CardContent>
              )}
            </Card>

            {/* Subscriptions */}
            <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} data-testid="subscriptions-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 cursor-pointer"
                onClick={() => setSubsExpanded(!subsExpanded)}>
                <CardTitle className={`flex items-center gap-2 text-base ${darkMode ? 'text-white' : ''}`}>
                  <CreditCard className="w-5 h-5 text-blue-500" /> Subscriptions
                </CardTitle>
                <div className="flex items-center gap-1">
                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {showBudgetValues ? formatCurrency(totalSubs) : '...'}/mo
                  </span>
                  {subsExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </CardHeader>
              {subsExpanded && (
                <CardContent className="pt-0">
                  {(budget.subscriptions || []).length > 0 ? (
                    <div className="space-y-1.5">
                      {(budget.subscriptions || []).map(sub => (
                        <div key={sub.id} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                          data-testid={`sub-${sub.id}`}>
                          <RotateCcw className={`w-3.5 h-3.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <div className="flex-1 min-w-0">
                            <span className={`font-medium truncate block ${darkMode ? 'text-gray-200' : ''}`}>{sub.name}</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{sub.frequency || 'monthly'}</span>
                              {sub.renewal_date && <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Renews: {sub.renewal_date}</span>}
                              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{sub.category}</span>
                            </div>
                          </div>
                          <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                            {showBudgetValues ? formatCurrency(sub.amount) : '...'}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className={`h-6 w-6 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : ''}`}>
                                <MoreHorizontal className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => { setEditingSub(sub); setShowSubDialog(true); }}>
                                <Pencil className="w-3 h-3 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteBillOrSub('sub', sub.id)} className="text-red-600">
                                <Trash2 className="w-3 h-3 mr-2" /> Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-xs text-center py-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No subscriptions tracked yet</p>
                  )}
                  <Button variant="outline" size="sm" className={`w-full mt-2 text-xs ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
                    onClick={() => { setEditingSub({ name: '', amount: '', frequency: 'monthly', renewal_date: '', category: 'Other' }); setShowSubDialog(true); }}
                    data-testid="add-sub-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Subscription
                  </Button>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* === DIALOGS === */}

      {/* New Work Table */}
      <Dialog open={showNewWorkTable} onOpenChange={setShowNewWorkTable}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Personal Work Table</DialogTitle>
            <DialogDescription>This will be a private workspace only visible to you</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Name</Label>
            <Input value={newWorkTableName} onChange={(e) => setNewWorkTableName(e.target.value)}
              placeholder="My Personal Ideas..." className="mt-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewWorkTable(false)}>Cancel</Button>
            <Button onClick={createPersonalWorkTable}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Quick List */}
      <Dialog open={showNewList} onOpenChange={setShowNewList}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Quick List</DialogTitle>
            <DialogDescription>A lightweight list for easy reference</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>List Name</Label>
              <Input value={newListName} onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g., Shopping, Call List, Follow-ups..." className="mt-1"
                onKeyDown={(e) => { if (e.key === 'Enter') createQuickList(); }} />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {LIST_COLORS.map(c => (
                  <button key={c.value} onClick={() => setNewListColor(c.value)}
                    className={`w-8 h-8 rounded-full ${c.bg} transition-transform ${newListColor === c.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'opacity-60 hover:opacity-100'}`}
                    title={c.name} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewList(false)}>Cancel</Button>
            <Button onClick={createQuickList}>Create List</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Settings */}
      <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your Budget Settings</DialogTitle>
            <DialogDescription>Track your monthly finances privately</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {[
              { label: 'Monthly Income', key: 'monthly_income' },
              { label: 'Monthly Expenses (non-bill)', key: 'monthly_expenses' },
            ].map(({ label, key }) => (
              <div key={key}>
                <Label>{label}</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={budget[key]}
                    onChange={(e) => setBudget(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                    className="pl-8" />
                </div>
              </div>
            ))}
            <div className="border-t pt-4">
              <Label>Savings Goal</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="number" value={budget.savings_goal}
                  onChange={(e) => setBudget(p => ({ ...p, savings_goal: parseFloat(e.target.value) || 0 }))} className="pl-8" />
              </div>
            </div>
            <div>
              <Label>Current Savings</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="number" value={budget.current_savings}
                  onChange={(e) => setBudget(p => ({ ...p, current_savings: parseFloat(e.target.value) || 0 }))} className="pl-8" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Cancel</Button>
            <Button onClick={() => { saveBudgetData(budget); setShowBudgetDialog(false); toast.success('Budget saved'); }}>Save Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBill?.id ? 'Edit' : 'Add'} Bill</DialogTitle>
            <DialogDescription>Track a recurring monthly bill</DialogDescription>
          </DialogHeader>
          {editingBill && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Bill Name</Label>
                <Input value={editingBill.name} onChange={(e) => setEditingBill(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Electric, Mortgage, Car Insurance..." className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="number" value={editingBill.amount}
                      onChange={(e) => setEditingBill(p => ({ ...p, amount: e.target.value }))} className="pl-8" />
                  </div>
                </div>
                <div>
                  <Label>Due Day of Month</Label>
                  <Input type="number" min="1" max="31" value={editingBill.due_day} className="mt-1"
                    onChange={(e) => setEditingBill(p => ({ ...p, due_day: parseInt(e.target.value) || '' }))} placeholder="e.g., 15" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editingBill.category} onValueChange={(v) => setEditingBill(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BILL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={editingBill.auto_pay}
                      onCheckedChange={(checked) => setEditingBill(p => ({ ...p, auto_pay: !!checked }))} />
                    <span className="text-sm">Auto-Pay <Zap className="w-3 h-3 inline text-amber-500" /></span>
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBillDialog(false)}>Cancel</Button>
            <Button onClick={() => saveBillOrSub('bill')}>Save Bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSub?.id ? 'Edit' : 'Add'} Subscription</DialogTitle>
            <DialogDescription>Track recurring subscription charges</DialogDescription>
          </DialogHeader>
          {editingSub && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Subscription Name</Label>
                <Input value={editingSub.name} onChange={(e) => setEditingSub(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Netflix, AutoCAD, Gym..." className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="number" value={editingSub.amount}
                      onChange={(e) => setEditingSub(p => ({ ...p, amount: e.target.value }))} className="pl-8" />
                  </div>
                </div>
                <div>
                  <Label>Frequency</Label>
                  <Select value={editingSub.frequency} onValueChange={(v) => setEditingSub(p => ({ ...p, frequency: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editingSub.category} onValueChange={(v) => setEditingSub(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SUB_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Renewal Date</Label>
                  <Input type="date" value={editingSub.renewal_date} className="mt-1"
                    onChange={(e) => setEditingSub(p => ({ ...p, renewal_date: e.target.value }))} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubDialog(false)}>Cancel</Button>
            <Button onClick={() => saveBillOrSub('sub')}>Save Subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
